import { useState, useEffect, useRef } from "react";
import { useNavigate, Navigate, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Question, Lightning, Warning, Sparkle, ArrowRight, X, FloppyDisk, ChatCircle, Hash } from "@phosphor-icons/react";
import MarkdownEditor from "../components/MarkdownEditor";
import SEOHead from "../components/SEOHead";
import { useDraft } from "../lib/useDraft";

export default function AskPage({ onOpenAI }) {
    const { user, loading: authLoading } = useAuth();
    const nav = useNavigate();
    const { t } = useTranslation();
    const [title, setTitle] = useState("");
    const [checklist, setChecklist] = useState({ searched: false, tried: false, evidence: false });
    const checklistDone = checklist.searched && checklist.tried && checklist.evidence;
    const [body, setBody] = useState("");
    const [tags, setTags] = useState("");
    const [loading, setLoading] = useState(false);
    const [dupes, setDupes] = useState([]);
    const [dupesMode, setDupesMode] = useState(null);
    const [dupesChecking, setDupesChecking] = useState(false);
    const [dupesDismissed, setDupesDismissed] = useState(false);
    const [allTags, setAllTags] = useState([]);
    const debounceRef = useRef(null);
    const tagInputRef = useRef(null);
    const [sp] = useSearchParams();
    const prefilledRef = useRef(false);

    useEffect(() => {
        const t = sp.get("title") || "", b = sp.get("body") || "", tg = sp.get("tags") || "";
        if (t || b || tg) {
            prefilledRef.current = true;
            if (t) setTitle(t);
            if (b) setBody(b);
            if (tg) setTags(tg);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const draftValue = { title, body, tags };
    const draftDirty = !!(title.trim() || body.trim() || tags.trim());
    const { hasDraft, restore, clear, savedAt } = useDraft("ask", draftValue, draftDirty);
    const [draftPromptShown, setDraftPromptShown] = useState(false);

    useEffect(() => {
        api.get("/tags?limit=60").then(({ data }) => setAllTags(data || [])).catch(() => {});
    }, []);

    useEffect(() => {
        if (hasDraft && !prefilledRef.current && !draftPromptShown && !title && !body && !tags) {
            setDraftPromptShown(true);
            const v = restore();
            if (v) {
                toast.success("Draft restored", { description: "Continued from your last unsaved question." });
                setTitle(v.title || "");
                setBody(v.body || "");
                setTags(v.tags || "");
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasDraft]);

    useEffect(() => {
        if (!user) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (title.trim().length < 12 || dupesDismissed) {
            setDupes([]);
            return;
        }
        debounceRef.current = setTimeout(async () => {
            setDupesChecking(true);
            try {
                const { data } = await api.post("/questions/check-duplicates", {
                    title: title.trim(),
                    body: body.trim().slice(0, 1500),
                    tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
                });
                setDupes(data.candidates || []);
                setDupesMode(data.mode || null);
            } catch { /* silent */ }
            setDupesChecking(false);
        }, 1200);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
    }, [title, body, tags, user, dupesDismissed]);

    if (authLoading) return null;
    if (!user) return <Navigate to="/login" replace />;

    const existingTags = tags.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
    const currentToken = tags.split(",").pop().trim().toLowerCase();
    const suggestions = currentToken
        ? allTags.filter((tg) => tg.name.toLowerCase().includes(currentToken) && !existingTags.includes(tg.name.toLowerCase())).slice(0, 6)
        : [];
    const popular = allTags.filter((tg) => !existingTags.includes(tg.name.toLowerCase())).slice(0, 10);

    const addTag = (name) => {
        const parts = tags.split(",").map((s) => s.trim()).filter(Boolean);
        if (parts.map((p) => p.toLowerCase()).includes(name.toLowerCase())) return;
        setTags([...parts, name].join(", ") + ", ");
        tagInputRef.current?.focus();
    };
    const pickSuggestion = (name) => {
        const parts = tags.split(",").slice(0, -1).map((s) => s.trim()).filter(Boolean);
        setTags([...parts, name].join(", ") + ", ");
        tagInputRef.current?.focus();
    };

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                title: title.trim(),
                body: body.trim(),
                tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
            };
            const { data } = await api.post("/questions", payload);
            toast.success("Question posted");
            clear();
            nav(`/q/${data.question_id}`);
        } catch (e) {
            toast.error(formatErr(e));
        }
        setLoading(false);
    };

    const inputCls = "w-full bg-[#0b0f19] ring-1 ring-white/10 text-slate-200 rounded-sm focus:ring-2 focus:ring-cyan-500/50 focus:outline-none transition-shadow";

    return (
        <div className="max-w-3xl">
            <SEOHead
                title="Ask a Question · Ethical Hacking Q&A"
                description="Ask the ethical hacking community — appsec, red team, blue team, cloud, OSINT, reverse engineering. Get answers from verified pros and earn karma."
                path="/ask"
                noindex={true}
            />
            <div className="flex items-center gap-3 mb-2">
                <div className="w-11 h-11 rounded-sm bg-cyan-500/10 ring-1 ring-cyan-500/30 flex items-center justify-center shrink-0">
                    <Question size={22} weight="duotone" className="text-cyan-400" />
                </div>
                <div>
                    <h1 className="text-2xl sm:text-3xl font-black tracking-tight leading-tight" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>{t("ask.title")}</h1>
                    <p className="text-sm text-slate-400 mt-0.5">{t("ask.subtitle")}</p>
                </div>
            </div>

            <form onSubmit={submit} className="hc-card p-6 sm:p-7 space-y-6 mt-5" data-testid="ask-form">
                <div>
                    <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block font-semibold">{t("ask.title_label")}</label>
                    <input
                        required
                        value={title}
                        onChange={(e) => { setTitle(e.target.value); setDupesDismissed(false); }}
                        placeholder={t("ask.title_placeholder")}
                        className={`${inputCls} text-base p-3.5`}
                        data-testid="ask-title-input"
                    />
                    {dupesChecking && (
                        <div className="mt-2 text-[10px] font-mono text-slate-500 flex items-center gap-1.5" data-testid="ask-dupes-checking">
                            <Sparkle size={10} weight="fill" className="text-purple-400 animate-pulse" /> SEC-MATCH is scanning for duplicates…
                        </div>
                    )}
                </div>

                {/* Duplicate-question alert */}
                {!dupesDismissed && dupes.length > 0 && (
                    <div className="bg-amber-500/[0.06] ring-1 ring-amber-500/30 rounded-sm p-4" data-testid="ask-dupes-panel">
                        <div className="flex items-start gap-2.5 mb-3">
                            <Warning size={15} weight="fill" className="text-amber-400 mt-0.5 shrink-0" />
                            <div className="flex-1">
                                <div className="text-sm font-bold text-amber-300" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>
                                    {dupesMode === "ai" ? "SEC-MATCH found possible duplicates" : "Possibly similar questions"}
                                </div>
                                <p className="text-[11px] text-slate-400 font-mono mt-0.5">
                                    // answering an existing thread earns karma faster than a duplicate.
                                </p>
                            </div>
                            <button onClick={() => setDupesDismissed(true)} className="text-slate-500 hover:text-slate-200 p-1" data-testid="ask-dupes-dismiss">
                                <X size={14} />
                            </button>
                        </div>
                        <div className="space-y-2">
                            {dupes.map((d) => (
                                <Link
                                    key={d.question_id}
                                    to={`/q/${d.question_id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-start gap-2.5 p-3 bg-[#0b0f19] ring-1 ring-white/8 hover:ring-amber-500/40 rounded-sm group transition-shadow"
                                    data-testid={`ask-dupe-${d.question_id}`}
                                >
                                    <ArrowRight size={12} className="text-amber-400 mt-1 shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-slate-200 group-hover:text-amber-300 truncate" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>{d.title}</div>
                                        {d.reason && <div className="text-[10px] text-slate-500 font-mono mt-0.5 line-clamp-1">// {d.reason}</div>}
                                    </div>
                                    <div className="text-[10px] text-slate-500 font-mono shrink-0 flex items-center gap-1">
                                        <ChatCircle size={10} /> {d.answer_count || 0} · {d.score || 0} pts
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}

                <div>
                    <div className="flex items-center justify-between mb-2">
                        <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{t("ask.body_label")}</label>
                        <button type="button" onClick={onOpenAI} className="text-[10px] uppercase tracking-widest text-purple-400 hover:text-purple-300 flex items-center gap-1" data-testid="ask-open-ai">
                            <Lightning size={11} weight="fill" /> {t("ask.ask_sec_ops")}
                        </button>
                    </div>
                    <MarkdownEditor
                        value={body}
                        onChange={setBody}
                        placeholder={t("ask.body_placeholder")}
                        minHeight="h-72"
                        testid="ask-body-editor"
                        required
                    />
                </div>

                <div>
                    <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block font-semibold">{t("ask.tags_label")}</label>
                    <div className="relative">
                        <Hash size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-600" />
                        <input
                            ref={tagInputRef}
                            value={tags}
                            onChange={(e) => setTags(e.target.value)}
                            placeholder={t("ask.tags_placeholder")}
                            className={`${inputCls} text-sm font-mono pl-9 pr-3 py-3`}
                            data-testid="ask-tags-input"
                            autoComplete="off"
                        />
                        {suggestions.length > 0 && (
                            <div className="absolute z-20 left-0 right-0 mt-1.5 bg-[#0d1424] ring-1 ring-white/10 rounded-sm shadow-2xl shadow-black/60 overflow-hidden py-1" data-testid="ask-tag-suggestions">
                                {suggestions.map((tg) => (
                                    <button
                                        key={tg.name}
                                        type="button"
                                        onClick={() => pickSuggestion(tg.name)}
                                        className="w-full flex items-center justify-between px-3.5 py-2 text-left hover:bg-white/5 transition-colors"
                                        data-testid={`ask-tag-sugg-${tg.name}`}
                                    >
                                        <span className="text-sm text-slate-200 font-mono flex items-center gap-1.5"><Hash size={11} className="text-cyan-400" />{tg.name}</span>
                                        <span className="text-[10px] text-slate-500 font-mono">{tg.count} uses</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    {popular.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid="ask-popular-tags">
                            <span className="text-[10px] uppercase tracking-widest text-slate-600 font-mono mr-1 self-center">popular:</span>
                            {popular.map((tg) => (
                                <button
                                    key={tg.name}
                                    type="button"
                                    onClick={() => addTag(tg.name)}
                                    className="text-[11px] font-mono px-2.5 py-1 rounded-full border border-slate-700 text-slate-400 hover:border-cyan-500/50 hover:text-cyan-300 transition-colors"
                                    data-testid={`ask-popular-${tg.name}`}
                                >
                                    #{tg.name}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="border border-white/10 bg-white/[0.02] rounded-sm p-3.5" data-testid="ask-originality-checklist">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">// before you post</div>
                    {[
                        ["searched", "I searched — this isn't already answered here"],
                        ["tried", "I tried it myself and describe what happened"],
                        ["evidence", "I included commands, errors or evidence (no screenshots of text)"],
                    ].map(([k, label]) => (
                        <label key={k} className="flex items-start gap-2.5 py-1.5 cursor-pointer group min-h-[36px]">
                            <input type="checkbox" checked={checklist[k]} data-testid={`ask-check-${k}`}
                                   onChange={(e) => setChecklist((c) => ({ ...c, [k]: e.target.checked }))}
                                   className="mt-0.5 accent-cyan-400 w-4 h-4 shrink-0" />
                            <span className="text-xs text-slate-400 group-hover:text-slate-300 transition-colors">{label}</span>
                        </label>
                    ))}
                </div>

                <div className="flex gap-2.5 items-center pt-1">
                    <button
                        type="submit"
                        disabled={loading || !checklistDone}
                        title={checklistDone ? undefined : "tick the checklist above first"}
                        className="bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold text-xs uppercase tracking-widest px-6 py-3 rounded-sm transition-colors disabled:opacity-50 shadow-lg"
                        data-testid="ask-submit-btn"
                    >
                        {loading ? t("ask.publishing") : t("ask.publish")}
                    </button>
                    <button
                        type="button"
                        onClick={() => nav(-1)}
                        className="bg-transparent ring-1 ring-white/10 hover:ring-slate-600 text-slate-400 text-xs uppercase tracking-widest px-6 py-3 rounded-sm transition-shadow"
                    >
                        {t("ask.cancel")}
                    </button>
                    {savedAt && (
                        <span className="ml-auto text-[10px] uppercase tracking-widest text-slate-500 font-mono flex items-center gap-1" data-testid="ask-draft-saved">
                            <FloppyDisk size={11} weight="duotone" /> {t("ask.draft_saved")}
                        </span>
                    )}
                </div>
            </form>
        </div>
    );
}
